
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle, PawPrint } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const UpdatePassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // Supabase sends recovery tokens in the URL hash fragment (#), not query params
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const type = hashParams.get('type');

    console.log('Password Reset Hash Parameters:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      type,
    });

    // If no hash tokens, check if user already has a valid session (e.g. routed via PASSWORD_RECOVERY event)
    if (!accessToken && !refreshToken && !type) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          console.log('User already has a valid session, allowing password update');
          setIsValidSession(true);
        } else {
          console.log('No session and no tokens — redirecting to auth');
          toast.error(t('updatePasswordPage.invalidOrExpiredLink'));
          navigate('/auth');
        }
      });
      return;
    }

    if (type && type !== 'recovery') {
      console.error('Invalid recovery type:', type);
      toast.error(t('updatePasswordPage.invalidLinkType'));
      navigate('/auth');
      return;
    }

    if (accessToken && refreshToken) {
      console.log('Setting session with tokens from hash...');
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      }).then(({ data, error }) => {
        if (error) {
          console.error('Error setting session:', error);
          toast.error(t('updatePasswordPage.invalidOrExpiredLink'));
          navigate('/auth');
        } else {
          console.log('Session set successfully:', data);
          setIsValidSession(true);
          // Clean the hash from the URL for security
          window.history.replaceState(null, '', window.location.pathname);
        }
      });
    } else if (type === 'recovery') {
      console.error('Recovery type found but missing tokens');
      toast.error(t('updatePasswordPage.invalidOrExpiredLink'));
      navigate('/auth');
    }
  }, [navigate, t]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 6) {
      return t('updatePasswordPage.minLength');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('updatePasswordPage.passwordsDoNotMatch'));
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      setIsSuccess(true);
      toast.success(t('settings.passwordUpdatedSuccess'));

      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/auth');
      }, 3000);
    } catch (error: any) {
      console.error('Password update error:', error);
      const msg = error.message || t('updatePasswordPage.failedToUpdate');
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isValidSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pet-purple mx-auto mb-4"></div>
              <p>{t('updatePasswordPage.validatingLink')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <CardTitle className="text-2xl text-green-700">{t('updatePasswordPage.successTitle')}</CardTitle>
            <CardDescription>{t('updatePasswordPage.successDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-gray-600">{t('updatePasswordPage.redirectMessage')}</p>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-pet-purple hover:bg-pet-purple/90"
            >
              {t('updatePasswordPage.goToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <h1 className="text-3xl font-bold text-pet-purple flex items-center">
              <div className="w-8 h-8 bg-pet-purple rounded-full flex items-center justify-center mr-2">
                <PawPrint className="h-4 w-4 text-white" />
              </div>
              <span className="petius-brand">Petius</span>
            </h1>
          </div>
          <CardTitle className="text-2xl text-center">{t('updatePasswordPage.title')}</CardTitle>
          <CardDescription className="text-center">{t('updatePasswordPage.subtitle')}</CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">{t('updatePasswordPage.newPassword')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('updatePasswordPage.placeholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <p className="text-xs text-gray-500">{t('updatePasswordPage.minLength')}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">{t('updatePasswordPage.confirmPassword')}</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder={t('updatePasswordPage.placeholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-pet-purple hover:bg-pet-purple/90"
              disabled={isLoading}
            >
              {isLoading ? t('updatePasswordPage.updating') : t('updatePasswordPage.submit')}
            </Button>

            <div className="text-center">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/auth')}
                className="text-sm"
              >
                {t('updatePasswordPage.backToLogin')}
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
};

export default UpdatePassword;
